import {
    JsonSchema, JsonSchemaProperty
} from './types';

/**
 * Generates a body object from a JSON schema with default values filled in
 * and all other fields set to empty values based on their type
 */
export function generateBodyFromSchema(schemaInput: string | Record<string, unknown>): string {
    if (!schemaInput) {
        return '';
    }

    try {
        let schema: JsonSchema;

        // Handle both string and object inputs
        if (typeof schemaInput === 'string') {
            if (!schemaInput.trim()) {
                return '';
            }
            schema = JSON.parse(schemaInput);
        } else {
            // Already an object, use it directly
            schema = schemaInput as unknown as JsonSchema;
        }

        const body = buildBodyFromSchema(schema);
        return JSON.stringify(body, null, 2);
    } catch (error) {
        console.error('Error generating body from schema:', error);
        return '';
    }
}

function buildBodyFromSchema(schema: JsonSchema | JsonSchemaProperty): Record<string, unknown> | unknown[] | null {
    if (schema.type === 'object' && schema.properties) {
        const obj: Record<string, unknown> = {};

        Object.entries(schema.properties).forEach(([key, property]) => {
            obj[key] = getValueForProperty(property);
        });

        return obj;
    } else if (schema.type === 'array' && schema.items) {
        // For arrays, return an empty array or an array with one example item
        if (schema.items.type === 'object') {
            return [buildBodyFromSchema(schema.items)];
        } else {
            return [getValueForProperty(schema.items)];
        }
    }

    return null;
}

function getValueForProperty(property: JsonSchemaProperty): unknown {
    // If there's a default value, use it
    if (property.default !== undefined) {
        return property.default;
    }

    // Otherwise, return empty value based on type
    switch (property.type) {
    case 'string':
        return '';
    case 'number':
        return 0;
    case 'boolean':
        return false;
    case 'null':
        return null;
    case 'array':
        if (property.items) {
            if (property.items.type === 'object') {
                return [buildBodyFromSchema(property.items)];
            } else {
                return [getValueForProperty(property.items)];
            }
        }
        return [];
    case 'object':
        if (property.properties) {
            return buildBodyFromSchema(property);
        }
        return {};
    default:
        return null;
    }
}
